import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { assign, getBoard, getPeople, setStatus } from '../lib/api';
import type { MilestoneRef, Person } from '../lib/api';
import { useAuth } from '../lib/auth';
import { columnById, columnOf, COLUMNS, underWatch } from '../lib/board';
import { Chip, DueChip, KindChip, PriorityChip } from '../components/Chips';
import type { WorkItem } from '../lib/types';

function initials(email: string | null): string {
  if (!email) return '';
  const name = email.split('@')[0];
  const parts = name.split(/[._-]+/).filter(Boolean);
  return ((parts[0]?.[0] ?? '') + (parts[1]?.[0] ?? '')).toUpperCase() || name.slice(0, 2).toUpperCase();
}

export default function BoardPage() {
  const { email, role } = useAuth();
  const canEdit = role === 'developer';

  const [items, setItems] = useState<WorkItem[]>([]);
  const [openTasks, setOpenTasks] = useState<Record<string, number>>({});
  const [milestones, setMilestones] = useState<MilestoneRef[]>([]);
  const [people, setPeople] = useState<Person[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dragging, setDragging] = useState<string | null>(null);
  const [over, setOver] = useState<string | null>(null);

  // filters
  const [q, setQ] = useState('');
  const [mine, setMine] = useState(false);
  const [kind, setKind] = useState('');
  const [component, setComponent] = useState('');
  const [milestone, setMilestone] = useState('');
  const [showDone, setShowDone] = useState(false);

  async function load(all: boolean) {
    setLoading(true);
    try {
      const b = await getBoard(all);
      setItems(b.items);
      setOpenTasks(b.openTasks);
      setMilestones(b.milestones ?? []);
      setError(null);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load(showDone);
  }, [showDone]);

  // Everyone signed in may assign, so everyone gets the people list.
  useEffect(() => {
    getPeople().then((p) => setPeople(p.people)).catch(() => setPeople([]));
  }, []);

  const components = useMemo(
    () => [...new Set(items.flatMap((i) => i.components))].filter((c) => !c.startsWith('experience:')).sort(),
    [items],
  );
  const msById = useMemo(() => new Map(milestones.map((m) => [m.id, m])), [milestones]);

  const visible = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return items.filter((i) => {
      if (mine && i.owner !== email) return false;
      if (kind && i.kind !== kind) return false;
      if (component && !i.components.includes(component)) return false;
      if (milestone && (milestone === 'none' ? i.milestone_id : i.milestone_id !== milestone)) return false;
      if (needle && !`${i.key} ${i.title} ${i.summary ?? ''}`.toLowerCase().includes(needle)) return false;
      return true;
    });
  }, [items, q, mine, kind, component, milestone, email]);

  // Move optimistically: the card lands where it was dropped straight away, and
  // snaps back with the server's reason if the database refuses.
  async function move(item: WorkItem, columnId: string) {
    const col = columnById(columnId);
    if (!col || columnOf(item) === columnId) return;
    const status = col.statusFor(item);
    const resolution = col.resolutionFor ? col.resolutionFor(item) : undefined;
    const before = items;
    setItems((cur) => cur.map((i) => (i.id === item.id ? { ...i, status, resolution: resolution ?? i.resolution } : i)));
    setError(null);
    try {
      const { item: saved } = await setStatus(item.key, status, resolution);
      setItems((cur) => cur.map((i) => (i.id === saved.id ? saved : i)));
    } catch (e) {
      setItems(before);
      setError((e as Error).message);
    }
  }

  async function setOwner(item: WorkItem, owner: string | null) {
    const before = items;
    setItems((cur) => cur.map((i) => (i.id === item.id ? { ...i, owner } : i)));
    try {
      const { item: saved } = await assign(item.key, owner);
      setItems((cur) => cur.map((i) => (i.id === saved.id ? saved : i)));
    } catch (e) {
      setItems(before);
      setError((e as Error).message);
    }
  }

  if (loading && !items.length) return <p className="loading">Loading the board...</p>;

  return (
    <>
      <div className="detail-head">
        <h1>Board</h1>
        <span className="count">{visible.length} shown</span>
      </div>

      <div className="filters">
        <input type="search" placeholder="Filter" value={q} onChange={(e) => setQ(e.target.value)} />
        <select value={kind} onChange={(e) => setKind(e.target.value)}>
          <option value="">Any kind</option>
          <option value="bug">Bugs</option>
          <option value="feature">Features</option>
          <option value="research">Research</option>
          <option value="chore">Chores</option>
        </select>
        <select value={component} onChange={(e) => setComponent(e.target.value)}>
          <option value="">Any component</option>
          {components.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </select>
        <select value={milestone} onChange={(e) => setMilestone(e.target.value)}>
          <option value="">Any milestone</option>
          <option value="none">No milestone</option>
          {milestones.map((m) => (
            <option key={m.id} value={m.id}>
              {m.title}
            </option>
          ))}
        </select>
        <label className="toggle">
          <input type="checkbox" checked={mine} onChange={(e) => setMine(e.target.checked)} /> Mine
        </label>
        <label className="toggle">
          <input type="checkbox" checked={showDone} onChange={(e) => setShowDone(e.target.checked)} /> Include finished
        </label>
      </div>

      {error ? <div className="error">{error}</div> : null}

      <div className="board">
        {COLUMNS.map((col) => {
          const cards = visible.filter((i) => columnOf(i) === col.id);
          if (!showDone && !cards.length && (col.id === 'done' || col.id === 'cancelled')) return null;
          return (
            <section
              key={col.id}
              className={`column${over === col.id ? ' over' : ''}`}
              onDragOver={(e) => {
                if (!canEdit || !dragging) return;
                e.preventDefault();
                setOver(col.id);
              }}
              onDragLeave={() => setOver((o) => (o === col.id ? null : o))}
              onDrop={(e) => {
                e.preventDefault();
                setOver(null);
                const item = items.find((i) => i.key === dragging);
                setDragging(null);
                if (item) move(item, col.id);
              }}
            >
              <header>
                <span className="col-label">{col.label}</span>
                <span className="count">{cards.length}</span>
                <span className="col-hint">{col.hint}</span>
              </header>

              {cards.map((item) => (
                <article
                  key={item.id}
                  className={`card${underWatch(item) ? ' watching' : ''}`}
                  draggable={canEdit}
                  onDragStart={() => setDragging(item.key)}
                  onDragEnd={() => {
                    setDragging(null);
                    setOver(null);
                  }}
                >
                  <Link to={`/item/${item.key}`} className="key">
                    {item.key}
                  </Link>
                  <Link to={`/item/${item.key}`} className="title">
                    {item.title}
                  </Link>
                  <div className="meta">
                    <KindChip kind={item.kind} />
                    <PriorityChip priority={item.priority} />
                    {item.components.slice(0, 2).map((c) => (
                      <Chip key={c}>{c.startsWith('experience:') ? 'experience' : c}</Chip>
                    ))}
                    {item.milestone_id && msById.has(item.milestone_id) ? (
                      <Chip className="milestone">{msById.get(item.milestone_id)!.title}</Chip>
                    ) : null}
                    <DueChip due={item.due_date} />
                    {openTasks[item.id] ? <span className="left">{openTasks[item.id]} left</span> : null}
                  </div>
                  <footer>
                    {canEdit ? (
                      <select
                        className="mover"
                        value={col.id}
                        title="Move to another column"
                        onChange={(e) => move(item, e.target.value)}
                      >
                        {COLUMNS.map((c) => (
                          <option key={c.id} value={c.id}>
                            {c.label}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <span className="col-hint">{col.label}</span>
                    )}
                    {people.length ? (
                      <select
                        className="owner-select"
                        value={item.owner ?? ''}
                        title="Assign"
                        onChange={(e) => setOwner(item, e.target.value || null)}
                      >
                        <option value="">unassigned</option>
                        {people.map((p) => (
                          <option key={p.email} value={p.email}>
                            {p.name}
                          </option>
                        ))}
                        {item.owner && !people.some((p) => p.email === item.owner) ? (
                          <option value={item.owner}>{item.owner}</option>
                        ) : null}
                      </select>
                    ) : item.owner ? (
                      <span className="avatar" title={item.owner}>
                        {initials(item.owner)}
                      </span>
                    ) : null}
                  </footer>
                </article>
              ))}
            </section>
          );
        })}
      </div>

      <p className="hint" style={{ marginTop: 18 }}>
        {canEdit
          ? 'Drag a card, or use the menu on it. To validate means shipped but not yet proven: a bug leaves it when sightings on the fixed version stay away.'
          : 'You can assign cards to people; moving them is for developers. Use Report to raise something.'}
      </p>
    </>
  );
}
